import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { XlsxService } from './../../shared/xlsx/xlsx.service';
import { RegistrosService } from './../../../../services/API/registros/registros.service';
import { Router } from '@angular/router';
import { AuthService } from './../../../../services/API/auth/auth.service';
import { MatSort, Sort } from '@angular/material/sort';
import { ScalesService } from './../../../../services/API/scales/scales.service';
import { NotificationServiceService } from './../../../../services/notification-service/notification-service.service';
import { EditMovimientoComponent } from './../../contenedores/subpages/shared/components/movimientos/edit-movimiento/edit-movimiento.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';

@Component({
    selector: 'app-ver-registros',
    templateUrl: './ver-registros.component.html',
    styleUrls: ['./ver-registros.component.css'],
    standalone: false
})
export class VerRegistrosComponent implements OnInit, OnDestroy {

  showProgressBar: boolean = true;
  displayedColumns = ['tipo', 'albaran', 'fechaInicio', 'fechaFin', 'transportistas', 'matriculas', 'scales', 'clasificado'];
  pages = [20, 50, 100];
  filter: string = '';
  dataSource = new MatTableDataSource<any>([]);
  totalRecords = 0;
  pageSize = 25;
  pageIndex = 0;
  sortColumn = 'fechaFin';
  sortDirection: 'asc' | 'desc' = 'desc';

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  clasificado: string = 'all';
  scales: any = [];
  scaleFilter: string = 'all';
  recordTypeFilter: string = 'all';
  movementTypeFilter: string = 'all';

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  constructor(
    private xlsxService: XlsxService,
    public authService: AuthService,
    private registrosService: RegistrosService,
    private scalesService: ScalesService,
    private notificationService: NotificationServiceService,
    private dialog: MatDialog,
    public router: Router
  ) { }

  ngOnInit(): void {
    this.userRolCheck('permiso_registros');
    this.paginator._intl.itemsPerPageLabel = 'Registros por página:';
    this.getScales();
    this.searchSubscription = this.searchSubject.pipe(debounceTime(400)).subscribe(() => {
      this.pageIndex = 0;
      this.loadRegistros();
    });
    this.loadRegistros();
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  get pesajeFiltersDisabled(): boolean {
    return this.recordTypeFilter === 'movement';
  }

  get movementTypeFilterDisabled(): boolean {
    return this.recordTypeFilter === 'pesaje';
  }

  userRolCheck(permission: string) {
    if (!this.authService.userRolCheck(permission)) {
      this.authService.logout();
    }
  }

  private buildQueryParams(exportData: boolean = false) {
    return {
      clasificado: this.pesajeFiltersDisabled ? 'all' : this.clasificado,
      page: this.pageIndex + 1,
      pageSize: this.pageSize,
      search: this.filter,
      scale: this.pesajeFiltersDisabled ? 'all' : this.scaleFilter,
      recordType: this.recordTypeFilter,
      movementType: this.movementTypeFilterDisabled ? 'all' : this.movementTypeFilter,
      sort: this.sortColumn,
      direction: this.sortDirection,
      export: exportData,
    };
  }

  loadRegistros() {
    this.showProgressBar = true;
    this.registrosService.getRegistrosPaginated(this.buildQueryParams()).pipe(
      finalize(() => {
        this.showProgressBar = false;
      })
    ).subscribe({
      next: (result) => {
        const parsed = this.parseRegistrosResponse(result);
        this.dataSource.data = parsed.data;
        this.totalRecords = parsed.total;
      },
      error: () => {
        this.dataSource.data = [];
        this.totalRecords = 0;
        this.notificationService.openSnackBar('No se pudieron cargar los registros');
      }
    });
  }

  private parseRegistrosResponse(result: any): { data: any[]; total: number } {
    if (Array.isArray(result)) {
      return { data: result, total: result.length };
    }
    return {
      data: result?.data ?? [],
      total: result?.total ?? 0,
    };
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadRegistros();
  }

  onSortChange(sort: Sort) {
    if (!sort.active || sort.direction === '') {
      return;
    }
    if (sort.active === this.sortColumn && sort.direction === this.sortDirection) {
      return;
    }
    this.sortColumn = sort.active;
    this.sortDirection = sort.direction;
    this.pageIndex = 0;
    this.loadRegistros();
  }

  applyFilter(event: Event) {
    this.filter = (event.target as HTMLInputElement).value;
    this.searchSubject.next(this.filter);
  }

  onClasificadoChange() {
    this.pageIndex = 0;
    this.loadRegistros();
  }

  applyScaleFilter() {
    this.pageIndex = 0;
    this.loadRegistros();
  }

  onRecordTypeChange() {
    if (this.recordTypeFilter === 'pesaje') {
      this.movementTypeFilter = 'all';
    }
    this.pageIndex = 0;
    this.loadRegistros();
  }

  onMovementTypeChange() {
    this.pageIndex = 0;
    this.loadRegistros();
  }

  reload() {
    this.loadRegistros();
  }

  saveExcel() {
    this.showProgressBar = true;
    this.registrosService.getRegistrosPaginated(this.buildQueryParams(true)).pipe(
      finalize(() => {
        this.showProgressBar = false;
      })
    ).subscribe({
      next: (result) => {
        const data = this.parseRegistrosResponse(result).data;
        const excelData = data.map(item => ({
          'Tipo': this.tipoLabel(item),
          'Nº Albarán': item['albaran'],
          'Fecha inicio': item['fechaInicio'],
          'Fecha final': item['fechaFin'],
          'Transportistas': item['transportistas'] || '',
          'Matriculas / Contenedores': this.matriculasExportLabel(item),
          'Báscula': item['scales'] || '',
          'Cliente': item['cliente'] || '',
          'Clasificado': item['clasificado'],
        }));
        this.xlsxService.saveExcelData(excelData, 'Registros');
      },
      error: () => {
        this.notificationService.openSnackBar('No se pudo exportar los registros');
      }
    });
  }

  verRegistro(element: any) {
    const type = element?.type || 'pesaje';
    const albaran = element?.albaran;

    if (type === 'pesaje') {
      this.router.navigateByUrl('/dashboard/ver-registro/' + albaran);
      return;
    }

    if (type === 'movement') {
      this.dialog.open(EditMovimientoComponent, {
        data: {
          movimiento: element.id,
          tipo: element.typeMovement,
          contenedorEntrega: element.contenedorEntregaID,
          contenedorRetirada: element.contenedorRetiradaID,
          fecha: element.fechaInicio,
          albaran: element.albaran,
          observaciones: element.observaciones,
          cliente: element.cliente,
          latitud: element.latitud,
          longitud: element.longitud,
        },
        width: '90%',
        maxWidth: '500px'
      });
    }
  }

  tipoLabel(element: any): string {
    if (element?.type === 'movement') {
      const labels: Record<string, string> = {
        entrega: 'Contenedor · Entrega',
        retirada: 'Contenedor · Retirada',
        cambio: 'Contenedor · Cambio',
      };
      return labels[element.typeMovement] || 'Contenedor';
    }
    return 'Pesaje';
  }

  matriculasExportLabel(element: any): string {
    if (element?.type === 'pesaje') {
      return element.matriculas || '';
    }
    if (element?.typeMovement === 'entrega') {
      return element.transportistas || '';
    }
    if (element?.typeMovement === 'retirada') {
      return element.matriculas || '';
    }
    if (element?.typeMovement === 'cambio') {
      return [element.transportistas, element.matriculas].filter(Boolean).join(' / ');
    }
    return element.matriculas || '';
  }

  getScales() {
    this.scalesService.getScales('true').subscribe((result: any) => {
      this.scales = result || [];
    });
  }

}
